/**
 * Activity Day Service
 *
 * Tracks distinct days a user is active in the app. One doc per LOCAL day
 * under userContent/{uid}/activityDays/{YYYY-MM-DD} (`date` matches the id).
 * `firstActivityAt` / `lastActivityAt` are UTC instants — analytics compares
 * those to Auth creation dates so returning-user reads don't mix local and
 * UTC calendar days.
 *
 * Used by the app_sessions goal type to count "practice days" and
 * available for future streak / content-refresh logic.
 */

import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  query,
  where,
  onSnapshot,
  Unsubscribe,
} from 'firebase/firestore';
import { db } from './firebase';
import type { ActivityDay, ActivityType, DailySessionProgress } from '../types';
import { getLocalDateString } from '../utils/dateUtils';

// ============================================
// COLLECTION REFERENCES
// ============================================

const getActivityDaysCollectionRef = (uid: string) =>
  collection(db, 'userContent', uid, 'activityDays');

const getActivityDayDocRef = (uid: string, date: string) =>
  doc(db, 'userContent', uid, 'activityDays', date);

// ============================================
// HELPERS
// ============================================

const getUtcIsoString = (): string => new Date().toISOString();

/**
 * Presence-flag activity types are recorded once-per-day rather than counted
 * per occurrence:
 *  - `moodCheckin` is stored once per day by design (the mood doc is date-keyed).
 *  - `reflections` is high-volume (a user may reflect on hundreds of transactions
 *    in one sitting), so we DON'T increment per reflection here — the per-day
 *    reflection *volume* is computed server-side from the source-of-truth
 *    `plaidTransactions.reflection` field in the analytics rollup. On this doc we
 *    only need a presence marker so a reflect-only day still counts toward
 *    `totalActivities` (active-day / streak / community logic).
 */
const PRESENCE_TYPES: ReadonlySet<ActivityType> = new Set(['moodCheckin', 'reflections']);

// Session cache so a flurry of reflections doesn't issue one Firestore
// read+write per reflection — once a presence type is marked for the day, later
// calls in the same session short-circuit. Keyed `${uid}:${date}:${type}`.
const markedPresenceThisSession = new Set<string>();

// ============================================
// RECORD ACTIVITY
// ============================================

/**
 * Record an activity for today. Get-or-creates the day doc, increments the
 * relevant counter (or sets the presence flag for presence types), and updates
 * timestamps.
 *
 * Returns whether this was the first activity of the day (useful for
 * triggering animations, streak logic, etc.).
 */
export const recordActivity = async (
  uid: string,
  type: ActivityType
): Promise<{ isFirstActivityToday: boolean }> => {
  const today = getLocalDateString();

  // Short-circuit redundant presence writes within the same session.
  if (PRESENCE_TYPES.has(type)) {
    const presenceKey = `${uid}:${today}:${type}`;
    if (markedPresenceThisSession.has(presenceKey)) {
      return { isFirstActivityToday: false };
    }
    markedPresenceThisSession.add(presenceKey);
  }

  const now = getUtcIsoString();
  const ref = getActivityDayDocRef(uid, today);

  const snap = await getDoc(ref);

  if (snap.exists()) {
    // Day doc exists — update it. The doc may have been CREATED by the
    // sequence engine (extended-session candidates land before any user
    // activity), so every activity field needs a default — spreading a
    // missing `activities` would produce NaN totals.
    const existing = snap.data() as ActivityDay;
    const activities: ActivityDay['activities'] = {
      focus: 0,
      exercises: 0,
      videos: 0,
      reflections: 0,
      journal: 0,
      moodCheckin: false,
      ...(existing.activities ?? {}),
    };

    if (type === 'moodCheckin') {
      activities.moodCheckin = true;
    } else if (type === 'reflections') {
      // Presence flag, not a count — volume is sourced server-side.
      activities.reflections = 1;
    } else {
      activities[type] = (activities[type] as number) + 1;
    }

    // Recount total (numeric fields only; moodCheckin counts as 1 if true)
    const totalActivities =
      activities.focus +
      activities.exercises +
      activities.videos +
      activities.reflections +
      activities.journal +
      (activities.moodCheckin ? 1 : 0);

    // Engine-created docs have no firstActivityAt yet — this IS the first
    // user activity of the day in that case.
    const isFirstActivityToday = !existing.firstActivityAt;
    await setDoc(ref, {
      ...existing,
      date: existing.date ?? today,
      firstActivityAt: existing.firstActivityAt ?? now,
      activities,
      totalActivities,
      lastActivityAt: now,
    });

    return { isFirstActivityToday };
  }

  // First activity of the day — create a new doc
  const activities: ActivityDay['activities'] = {
    focus: 0,
    exercises: 0,
    videos: 0,
    reflections: 0,
    journal: 0,
    moodCheckin: false,
  };

  if (type === 'moodCheckin') {
    activities.moodCheckin = true;
  } else {
    activities[type] = 1;
  }

  const totalActivities =
    activities.focus +
    activities.exercises +
    activities.videos +
    activities.reflections +
    activities.journal +
    (activities.moodCheckin ? 1 : 0);

  const newDay: ActivityDay = {
    date: today,
    firstActivityAt: now,
    lastActivityAt: now,
    activities,
    totalActivities,
  };

  await setDoc(ref, newDay);

  return { isFirstActivityToday: true };
};

// ============================================
// RECORD LOGIN (no activity — just ensures a day doc exists)
// ============================================

/**
 * Ensure an activityDays doc exists for today. Called on login so that
 * e_sessions_to_date accurately counts days the user opened the app,
 * even if they don't complete any activities.
 */
export const recordLogin = async (uid: string): Promise<void> => {
  const today = getLocalDateString();
  const ref = getActivityDayDocRef(uid, today);

  const snap = await getDoc(ref);
  if (snap.exists()) return; // Already tracked today

  const now = getUtcIsoString();
  const newDay: ActivityDay = {
    date: today,
    firstActivityAt: now,
    lastActivityAt: now,
    activities: {
      focus: 0,
      exercises: 0,
      videos: 0,
      reflections: 0,
      journal: 0,
      moodCheckin: false,
    },
    totalActivities: 0,
  };

  await setDoc(ref, newDay);
};

// ============================================
// SUBSCRIPTIONS
// ============================================

/**
 * Subscribe to activity days from `startDate` onward (real-time).
 * Used by the goal calculator to count practice days in a goal period.
 */
/**
 * Merge guided daily-session progress onto TODAY's activity-day doc.
 * Day-keyed, so it auto-resets at local midnight. Fire-and-forget safe.
 */
export const saveDailySessionProgress = async (
  uid: string,
  patch: DailySessionProgress
): Promise<void> => {
  const date = getLocalDateString();
  try {
    await setDoc(
      getActivityDayDocRef(uid, date),
      { date, dailySession: patch },
      { merge: true }
    );
  } catch (error) {
    console.error('Error saving daily session progress:', error);
  }
};

export const subscribeToActivityDays = (
  uid: string,
  startDate: string,
  callback: (days: ActivityDay[]) => void
): Unsubscribe => {
  const q = query(
    getActivityDaysCollectionRef(uid),
    where('date', '>=', startDate)
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const days = snapshot.docs.map((d) => d.data() as ActivityDay);
      callback(days);
    },
    (error) => {
      console.error('Error subscribing to activity days:', error);
      callback([]);
    }
  );
};

// ============================================
// ONE-SHOT QUERIES
// ============================================

/**
 * Get count of activity day docs in a date range (inclusive).
 * Useful for non-reactive contexts or initial load.
 */
export const getActivityDayCount = async (
  uid: string,
  startDate: string,
  endDate?: string
): Promise<number> => {
  const constraints = [where('date', '>=', startDate)];
  if (endDate) {
    constraints.push(where('date', '<=', endDate));
  }
  const q = query(getActivityDaysCollectionRef(uid), ...constraints);
  const snapshot = await getDocs(q);
  return snapshot.size;
};
